"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SentrySpan = void 0;

var _currentScopes = require("../currentScopes.js");

var _debugBuild = require("../debug-build.js");

var _envelope = require("../envelope.js");

var _metricSummary = require("../metrics/metric-summary.js");

var _semanticAttributes = require("../semanticAttributes.js");

var _logger = require("../utils-hoist/logger.js");

var _object = require("../utils-hoist/object.js");

var _propagationContext = require("../utils-hoist/propagationContext.js");

var _time2 = require("../utils-hoist/time.js");

var _spanUtils = require("../utils/spanUtils.js");

var _dynamicSamplingContext = require("./dynamicSamplingContext.js");

var _logSpans = require("./logSpans.js");

var _measurement = require("./measurement.js");

var _utils = require("./utils.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const MAX_SPAN_COUNT = 1000;
/**
 * Span contains all data about a span
 */

class SentrySpan {
  /** Epoch timestamp in seconds when the span started. */

  /** Epoch timestamp in seconds when the span ended. */

  /** Internal keeper of the status */

  /** The timed events added to this span. */

  /** if true, treat span as a standalone span (not part of a transaction) */

  /**
   * You should never call the constructor manually, always use `Sentry.startSpan()`
   * or other span methods.
   * @internal
   * @hideconstructor
   * @hidden
   */
  constructor(spanContext = {}) {
    this._traceId = spanContext.traceId || (0, _propagationContext.generateTraceId)();
    this._spanId = spanContext.spanId || (0, _propagationContext.generateSpanId)();
    this._startTime = spanContext.startTimestamp || (0, _time2.timestampInSeconds)();
    this._attributes = {};
    this.setAttributes(_objectSpread({
      [_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'manual',
      [_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_OP]: spanContext.op
    }, spanContext.attributes));
    this._name = spanContext.name;

    if (spanContext.parentSpanId) {
      this._parentSpanId = spanContext.parentSpanId;
    } // We want to include booleans as well here


    if ('sampled' in spanContext) {
      this._sampled = spanContext.sampled;
    }

    if (spanContext.endTimestamp) {
      this._endTime = spanContext.endTimestamp;
    }

    this._events = [];
    this._isStandaloneSpan = spanContext.isStandalone; // If the span is already ended, ensure we finalize the span immediately

    if (this._endTime) {
      this._onSpanEnded();
    }
  }
  /**
   * This should generally not be used,
   * but it is needed for being compliant with the OTEL Span interface.
   *
   * @hidden
   * @internal
   */


  addLink(_link) {
    return this;
  }
  /**
   * This should generally not be used,
   * but it is needed for being compliant with the OTEL Span interface.
   *
   * @hidden
   * @internal
   */


  addLinks(_links) {
    return this;
  }
  /**
   * This should generally not be used,
   * but it is needed for being compliant with the OTEL Span interface.
   *
   * @hidden
   * @internal
   */


  recordException(_exception, _time) {// noop
  }
  /** @inheritdoc */


  spanContext() {
    const spanId = this._spanId,
          traceId = this._traceId,
          sampled = this._sampled;
    return {
      spanId,
      traceId,
      traceFlags: sampled ? _spanUtils.TRACE_FLAG_SAMPLED : _spanUtils.TRACE_FLAG_NONE
    };
  }
  /** @inheritdoc */


  setAttribute(key, value) {
    if (value === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this._attributes[key];
    } else {
      this._attributes[key] = value;
    }

    return this;
  }
  /** @inheritdoc */


  setAttributes(attributes) {
    Object.keys(attributes).forEach(key => this.setAttribute(key, attributes[key]));
    return this;
  }
  /**
   * This should generally not be used,
   * but we need it for browser tracing where we want to adjust the start time afterwards.
   * USE THIS WITH CAUTION!
   *
   * @hidden
   * @internal
   */


  updateStartTime(timeInput) {
    this._startTime = (0, _spanUtils.spanTimeInputToSeconds)(timeInput);
  }
  /**
   * @inheritDoc
   */


  setStatus(value) {
    this._status = value;
    return this;
  }
  /**
   * @inheritDoc
   */


  updateName(name) {
    this._name = name;
    this.setAttribute(_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'custom');
    return this;
  }
  /** @inheritdoc */


  end(endTimestamp) {
    // If already ended, skip
    if (this._endTime) {
      return;
    }

    this._endTime = (0, _spanUtils.spanTimeInputToSeconds)(endTimestamp);
    (0, _logSpans.logSpanEnd)(this);

    this._onSpanEnded();
  }
  /**
   * Get JSON representation of this span.
   *
   * @hidden
   * @internal This method is purely for internal purposes and should not be used outside
   * of SDK code. If you need to get a JSON representation of a span,
   * use `spanToJSON(span)` instead.
   */


  getSpanJSON() {
    return (0, _object.dropUndefinedKeys)({
      data: this._attributes,
      description: this._name,
      op: this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_OP],
      parent_span_id: this._parentSpanId,
      span_id: this._spanId,
      start_timestamp: this._startTime,
      status: (0, _spanUtils.getStatusMessage)(this._status),
      timestamp: this._endTime,
      trace_id: this._traceId,
      origin: this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN],
      _metrics_summary: (0, _metricSummary.getMetricSummaryJsonForSpan)(this),
      profile_id: this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_PROFILE_ID],
      exclusive_time: this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME],
      measurements: (0, _measurement.timedEventsToMeasurements)(this._events),
      is_segment: this._isStandaloneSpan && (0, _spanUtils.getRootSpan)(this) === this || undefined,
      segment_id: this._isStandaloneSpan ? (0, _spanUtils.getRootSpan)(this).spanContext().spanId : undefined
    });
  }
  /** @inheritdoc */


  isRecording() {
    return !this._endTime && !!this._sampled;
  }
  /**
   * @inheritdoc
   */


  addEvent(name, attributesOrStartTime, startTime) {
    _debugBuild.DEBUG_BUILD && _logger.logger.log('[Tracing] Adding an event to span:', name);
    const time = isSpanTimeInput(attributesOrStartTime) ? attributesOrStartTime : startTime || (0, _time2.timestampInSeconds)();
    const attributes = isSpanTimeInput(attributesOrStartTime) ? {} : attributesOrStartTime || {};
    const event = {
      name,
      time: (0, _spanUtils.spanTimeInputToSeconds)(time),
      attributes
    };

    this._events.push(event);

    return this;
  }
  /**
   * This method should generally not be used,
   * but for now we need a way to publicly check if the `_isStandaloneSpan` flag is set.
   * USE THIS WITH CAUTION!
   * @internal
   * @hidden
   * @experimental
   */


  isStandaloneSpan() {
    return !!this._isStandaloneSpan;
  }
  /** Emit `spanEnd` when the span is ended. */


  _onSpanEnded() {
    const client = (0, _currentScopes.getClient)();

    if (client) {
      client.emit('spanEnd', this);
    } // A segment span is basically the root span of a local span tree.
    // So for now, this is either what we previously refer to as the root span,
    // or a standalone span.


    const isSegmentSpan = this._isStandaloneSpan || this === (0, _spanUtils.getRootSpan)(this);

    if (!isSegmentSpan) {
      return;
    } // if this is a standalone span, we send it immediately


    if (this._isStandaloneSpan) {
      if (this._sampled) {
        sendSpanEnvelope((0, _envelope.createSpanEnvelope)([this], client));
      } else {
        _debugBuild.DEBUG_BUILD && _logger.logger.log('[Tracing] Discarding standalone span because its trace was not chosen to be sampled.');

        if (client) {
          client.recordDroppedEvent('sample_rate', 'span');
        }
      }

      return;
    }

    const transactionEvent = this._convertSpanToTransaction();

    if (transactionEvent) {
      const scope = (0, _utils.getCapturedScopesOnSpan)(this).scope || (0, _currentScopes.getCurrentScope)();
      scope.captureEvent(transactionEvent);
    }
  }
  /**
   * Finish the transaction & prepare the event to send to Sentry.
   */


  _convertSpanToTransaction() {
    // We can only convert finished spans
    if (!isFullFinishedSpan((0, _spanUtils.spanToJSON)(this))) {
      return undefined;
    }

    if (!this._name) {
      _debugBuild.DEBUG_BUILD && _logger.logger.warn('Transaction has no name, falling back to `<unlabeled transaction>`.');
      this._name = '<unlabeled transaction>';
    }

    const _getCapturedScopesOnS = (0, _utils.getCapturedScopesOnSpan)(this),
          capturedSpanScope = _getCapturedScopesOnS.scope,
          capturedSpanIsolationScope = _getCapturedScopesOnS.isolationScope;

    const scope = capturedSpanScope || (0, _currentScopes.getCurrentScope)();
    const client = scope.getClient() || (0, _currentScopes.getClient)();

    if (this._sampled !== true) {
      // At this point if `sampled !== true` we want to discard the transaction.
      _debugBuild.DEBUG_BUILD && _logger.logger.log('[Tracing] Discarding transaction because its trace was not chosen to be sampled.');

      if (client) {
        client.recordDroppedEvent('sample_rate', 'transaction');
      }

      return undefined;
    } // The transaction span itself as well as any potential standalone spans should be filtered out


    const finishedSpans = (0, _spanUtils.getSpanDescendants)(this).filter(span => span !== this && !isStandaloneSpan(span));
    const spans = finishedSpans.map(span => (0, _spanUtils.spanToJSON)(span)).filter(isFullFinishedSpan);
    const source = this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]; // remove internal root span attributes we don't need to send.

    /* eslint-disable @typescript-eslint/no-dynamic-delete */

    delete this._attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_CUSTOM_SPAN_NAME];
    spans.forEach(span => {
      span.data && delete span.data[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_CUSTOM_SPAN_NAME];
    }); // eslint-enabled-next-line @typescript-eslint/no-dynamic-delete

    const transaction = _objectSpread({
      contexts: {
        trace: (0, _spanUtils.spanToTransactionTraceContext)(this)
      },
      spans: // spans.sort() mutates the array, but `spans` is already a copy so we can safely do this here
      // we do not use spans anymore after this point
      spans.length > MAX_SPAN_COUNT ? spans.sort((a, b) => a.start_timestamp - b.start_timestamp).slice(0, MAX_SPAN_COUNT) : spans,
      start_timestamp: this._startTime,
      timestamp: this._endTime,
      transaction: this._name,
      type: 'transaction',
      sdkProcessingMetadata: _objectSpread({
        capturedSpanScope,
        capturedSpanIsolationScope
      }, (0, _object.dropUndefinedKeys)({
        dynamicSamplingContext: (0, _dynamicSamplingContext.getDynamicSamplingContextFromSpan)(this)
      })),
      _metrics_summary: (0, _metricSummary.getMetricSummaryJsonForSpan)(this)
    }, source && {
      transaction_info: {
        source
      }
    });

    const measurements = (0, _measurement.timedEventsToMeasurements)(this._events);
    const hasMeasurements = measurements && Object.keys(measurements).length;

    if (hasMeasurements) {
      _debugBuild.DEBUG_BUILD && _logger.logger.log('[Measurements] Adding measurements to transaction event', JSON.stringify(measurements, undefined, 2));
      transaction.measurements = measurements;
    }

    return transaction;
  }

}

exports.SentrySpan = SentrySpan;

function isSpanTimeInput(value) {
  return value && typeof value === 'number' || value instanceof Date || Array.isArray(value);
} // We want to filter out any incomplete SpanJSON objects


function isFullFinishedSpan(input) {
  return !!input.start_timestamp && !!input.timestamp && !!input.span_id && !!input.trace_id;
}
/** `SentrySpan`s can be sent as a standalone span rather than belonging to a transaction */


function isStandaloneSpan(span) {
  return span instanceof SentrySpan && span.isStandaloneSpan();
}
/**
 * Sends a `SpanEnvelope`.
 *
 * Note: If the envelope's spans are dropped, e.g. via `beforeSendSpan`,
 * the envelope will not be sent either.
 */


function sendSpanEnvelope(envelope) {
  const client = (0, _currentScopes.getClient)();

  if (!client) {
    return;
  }

  const spanItems = envelope[1];

  if (!spanItems || spanItems.length === 0) {
    client.recordDroppedEvent('before_send', 'span');
    return;
  } // sendEnvelope should not throw
  // eslint-disable-next-line @typescript-eslint/no-floating-promises


  client.sendEnvelope(envelope);
}